import { UseFormReturn } from "react-hook-form";
import { FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { PrenatalIntakeFormData, HEALTH_CONDITIONS } from "../PrenatalIntakeFormSchema";

interface HealthInfoSectionProps {
  form: UseFormReturn<PrenatalIntakeFormData>;
}

export function HealthInfoSection({ form }: HealthInfoSectionProps) {
  const hasPCP = form.watch("has_pcp");
  const hasDentalIssues = form.watch("dental_issues");

  return (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold border-b pb-2">Section IV: Health Information</h3>

      {/* Primary Care Physician */}
      <div className="space-y-4">
        <FormField
          control={form.control}
          name="has_pcp"
          render={({ field }) => (
            <FormItem className="flex items-start space-x-3 space-y-0">
              <FormControl>
                <Checkbox
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
              <div className="space-y-1 leading-none">
                <FormLabel>I have a Primary Care Physician (PCP)</FormLabel>
              </div>
            </FormItem>
          )}
        />

        {hasPCP && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 ml-6">
            <FormField
              control={form.control}
              name="pcp_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>PCP Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Doctor's name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="pcp_phone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>PCP Phone</FormLabel>
                  <FormControl>
                    <Input type="tel" placeholder="(555) 555-5555" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        )}
      </div>

      {/* Health Conditions */}
      <FormField
        control={form.control}
        name="health_conditions"
        render={() => (
          <FormItem>
            <FormLabel>Conditions requiring ongoing medical care</FormLabel>
            <FormDescription>Check all that apply. * indicates risk factors for PNCC eligibility</FormDescription>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2">
              {HEALTH_CONDITIONS.map((condition) => (
                <FormField
                  key={condition.value}
                  control={form.control}
                  name="health_conditions"
                  render={({ field }) => (
                    <FormItem className="flex items-center space-x-2 space-y-0">
                      <FormControl>
                        <Checkbox
                          checked={field.value?.includes(condition.value)}
                          onCheckedChange={(checked) => {
                            const currentValue = field.value || [];
                            if (checked) {
                              field.onChange([...currentValue, condition.value]);
                            } else {
                              field.onChange(currentValue.filter((v) => v !== condition.value));
                            }
                          }}
                        />
                      </FormControl>
                      <FormLabel className="text-sm font-normal">{condition.label}</FormLabel>
                    </FormItem>
                  )}
                />
              ))}
            </div>
            <FormMessage />
          </FormItem>
        )}
      />

      {form.watch("health_conditions")?.includes("other") && (
        <FormField
          control={form.control}
          name="other_health_conditions"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Other health conditions</FormLabel>
              <FormControl>
                <Textarea 
                  placeholder="Describe other conditions..." 
                  className="min-h-[60px]"
                  {...field} 
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      )}

      {/* STI Screening */}
      <FormField
        control={form.control}
        name="sti_screened"
        render={({ field }) => (
          <FormItem className="flex items-start space-x-3 space-y-0">
            <FormControl>
              <Checkbox
                checked={field.value}
                onCheckedChange={field.onChange}
              />
            </FormControl>
            <div className="space-y-1 leading-none">
              <FormLabel>Screened for STIs (including HIV and syphilis) during this pregnancy</FormLabel>
            </div>
          </FormItem>
        )}
      />

      {/* Dental Health */}
      <div className="space-y-4 pt-4 border-t">
        <h4 className="font-medium">Dental Health</h4>
        
        <FormField
          control={form.control}
          name="dental_visits_last_two_years"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Dental visits in the last two years</FormLabel>
              <FormControl>
                <Input 
                  type="number" 
                  min="0" 
                  placeholder="Number of visits"
                  {...field}
                  onChange={(e) => field.onChange(parseInt(e.target.value) || 0)}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="dental_issues"
          render={({ field }) => (
            <FormItem className="flex items-start space-x-3 space-y-0">
              <FormControl>
                <Checkbox
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
              <div className="space-y-1 leading-none">
                <FormLabel>Painful or loose teeth, bleeding gums, or bad taste/smell in mouth</FormLabel>
              </div>
            </FormItem>
          )}
        />

        {hasDentalIssues && (
          <FormField
            control={form.control}
            name="dental_issues_notes"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Please describe dental issues</FormLabel>
                <FormControl>
                  <Textarea 
                    placeholder="Describe your dental concerns..." 
                    className="min-h-[60px]"
                    {...field} 
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}
      </div>
    </div>
  );
}
